import { zodResolver } from '@hookform/resolvers/zod'
import {
  ErrorCode,
  type LoginRequest,
  PLATFORM_HUB_ERRORS,
  PLATFORM_HUB_START_PATH,
  type PlatformHubError,
  platformLoginSchema,
} from '@sahab/contracts'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Eye, EyeOff, LayoutGrid } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { ClientErrorCode, isApiError } from '../../lib/api'
import { errorMessage } from '../../lib/errors'
import { translateMessage, zodErrorMap } from '../../lib/validation'
import { Button, buttonClasses } from '../../ui/button'
import { Alert } from '../../ui/feedback'
import { Field } from '../../ui/field'
import { Input } from '../../ui/input'
import { useDocumentTitle } from '../shell/document-title'
import { LanguageSwitch, ThemeMenu } from '../shell/preferences-menu'
import { Wordmark } from '../shell/wordmark'
import { ConsoleBadge } from './console-layout'
import { safePlatformRedirect, usePlatformLogin } from './session'

type LoginInput = z.input<typeof platformLoginSchema>

export const PLATFORM_DISABLED = 'PLATFORM_DISABLED'

/** Maps console sign-in failures to what the operator can do about them. */
function useLoginErrorMessage() {
  const { t } = useTranslation('platform')
  const { t: tc, i18n } = useTranslation()
  return (error: unknown): string => {
    if (!isApiError(error)) return tc('errors.unexpected')
    if (error.status === 401) return t('login.errors.invalidCredentials')
    if (error.code === PLATFORM_DISABLED) return t('login.errors.disabled')
    if (error.code === ErrorCode.LOCAL_LOGIN_DISABLED) return t('login.hub.only')
    if (error.code === ErrorCode.RATE_LIMITED || error.status === 429) return t('login.errors.rateLimited')
    if (error.code === ErrorCode.VALIDATION_FAILED) return t('login.errors.invalidInput')
    if (error.code === ClientErrorCode.NETWORK_ERROR) return tc('errors.network')
    return errorMessage(tc, i18n.language, error)
  }
}

export function PlatformLoginForm({ onSignedIn }: { onSignedIn: () => void | Promise<void> }) {
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation()
  const login = usePlatformLogin()
  const toMessage = useLoginErrorMessage()
  const [showPassword, setShowPassword] = useState(false)
  const alertRef = useRef<HTMLDivElement>(null)
  const resolver = useMemo(() => zodResolver(platformLoginSchema, { error: zodErrorMap(tc) }), [tc])
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput, unknown, LoginRequest>({ resolver, defaultValues: { email: '', password: '' } })

  useEffect(() => {
    if (login.error) alertRef.current?.focus()
  }, [login.error])

  const submit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values)
      await onSignedIn()
    } catch {
      // Shown from login.error below.
    }
  })

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      {login.error ? (
        <div ref={alertRef} tabIndex={-1} className="outline-none">
          <Alert tone="danger">{toMessage(login.error)}</Alert>
        </div>
      ) : null}
      <Field label={t('email')} error={translateMessage(tc, errors.email?.message)}>
        <Input
          type="email"
          dir="ltr"
          autoComplete="username"
          inputMode="email"
          autoFocus
          {...register('email')}
        />
      </Field>
      <Field label={t('password')} error={translateMessage(tc, errors.password?.message)}>
        <Input
          type={showPassword ? 'text' : 'password'}
          dir="ltr"
          autoComplete="current-password"
          {...register('password')}
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('hidePassword') : t('showPassword')}
              aria-pressed={showPassword}
              className="flex size-6 items-center justify-center rounded-xs text-ink-muted hover:text-ink"
            >
              {showPassword ? (
                <EyeOff aria-hidden className="size-4" />
              ) : (
                <Eye aria-hidden className="size-4" />
              )}
            </button>
          }
        />
      </Field>
      <Button type="submit" variant="primary" className="mt-1 w-full" loading={isSubmitting}>
        {isSubmitting ? t('submitting') : t('submit')}
      </Button>
    </form>
  )
}

const route = getRouteApi('/platform/login')

/** Why a cloudTech Hub sign-in came back (`?sso_error=`, ADR-042), in words. */
const HUB_ERROR_MESSAGES = {
  no_superadmin: 'login.hub.errors.noSuperAdmin',
  not_mfa: 'login.hub.errors.notMfa',
  state: 'login.hub.errors.state',
  hub_denied: 'login.hub.errors.hubDenied',
  sso_not_configured: 'login.hub.errors.notConfigured',
  sso_failed: 'login.hub.errors.failed',
} as const satisfies Record<PlatformHubError, string>

const isHubError = (code: string): code is PlatformHubError =>
  (PLATFORM_HUB_ERRORS as readonly string[]).includes(code)

/**
 * /platform/login: the operators' sign-in, set apart from a company's by the Super admin mark. With
 * cloudTech Hub configured on the server it offers "Sign in with cloudTech Hub" too, and with the
 * password sign-in turned off (SUPERADMIN_LOCAL_LOGIN=false) only that (ADR-042).
 */
export function PlatformLoginPage() {
  const { t } = useTranslation('platform')
  const router = useRouter()
  const { redirect: target, sso_error: ssoError } = route.useSearch()
  const methods = route.useLoaderData() ?? { password: true, hub: false }
  const hubError =
    ssoError === undefined ? null : HUB_ERROR_MESSAGES[isHubError(ssoError) ? ssoError : 'sso_failed']
  useDocumentTitle(t('login.title'))
  return (
    <div data-slot="shell" data-surface="auth" className="flex min-h-dvh flex-col bg-canvas">
      <header className="flex items-center justify-end gap-1 px-4 py-3">
        <ThemeMenu />
        <LanguageSwitch />
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pt-[8vh] pb-16">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex flex-col items-center gap-2">
            <Wordmark />
            <ConsoleBadge />
          </div>
          <div className="rounded-panel border border-rule bg-surface panel px-6 py-6">
            <h1 className="font-display text-xl font-medium text-ink">{t('login.title')}</h1>
            <p className="mt-1 mb-5 text-sm text-ink-muted">{t('login.subtitle')}</p>
            <div className="flex flex-col gap-4">
              {hubError ? <Alert tone="danger">{t(hubError)}</Alert> : null}
              {methods.hub ? (
                // A full page load, not a router link: the API answers with a redirect to the Hub.
                <a
                  href={PLATFORM_HUB_START_PATH}
                  className={buttonClasses(methods.password ? 'secondary' : 'primary', 'md', 'w-full')}
                >
                  <LayoutGrid aria-hidden />
                  {t('login.hub.button')}
                </a>
              ) : null}
              {methods.hub && methods.password ? (
                <div className="flex items-center gap-3 text-xs text-ink-muted">
                  <span aria-hidden className="h-px flex-1 bg-rule" />
                  {t('login.hub.or')}
                  <span aria-hidden className="h-px flex-1 bg-rule" />
                </div>
              ) : null}
              {methods.password ? (
                <PlatformLoginForm
                  onSignedIn={() => router.navigate({ href: safePlatformRedirect(target), replace: true })}
                />
              ) : (
                <p className="text-sm text-ink-muted">
                  {methods.hub ? t('login.hub.only') : t('login.hub.none')}
                </p>
              )}
            </div>
          </div>
          <p className="mt-4 text-center text-xs text-ink-muted">{t('login.help')}</p>
        </div>
      </main>
    </div>
  )
}
